import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import {
  getBillingContractBillsAsync,
  getBillsPaymentData,
  getContractAsync,
  getContractsAsync,
  getExpenseProofAsync,
  getPaymentData,
  patchContractCancelAsync,
  postBillingAuthenticationAsync,
  postExpenseProofAsync,
  postPayletterPayment,
} from "src/api/contract/contract-api";
import { BillModel, BillingContractBillModel, ContractModel, MemberContractModel, isFreeContract } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import { useMediaQueries } from "src/pages/hooks/media-query";
import { useNavigateGoBack } from "src/pages/hooks/navigate-go-back";
import usePostMessage from "src/pages/hooks/post-message";
import { useMemberInfoState } from "src/recoil/member/hook";
import { YmdFormat, emailFormat, linePhoneFormat } from "src/utils/common-util";
import AccountInfo from "./components/AccountInfo";
import BasicInfo from "./components/BasicInfo";
import ContractInfo from "./components/ContractInfo";
import MyPaymentStatus from "./components/MyPaymentStatus";
import PaymentInfo from "./components/PaymentInfo";

import _ from "lodash";
import moment from "moment";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { ConfirmModal } from "src/components";
import { Modal } from "src/pages/contract/contract-types";
import { Modal as VisitorModal } from "src/pages/visitor/visitor-types";

import { getReservationsAsync } from "src/api/reservation/reservation-api";
import IssueModalRenderer from "src/pages/contract/apply/components/IssueModalRenderer";
import ServiceTypePaymentInfo from "../serviceTypePayment/ServiceTypePaymentInfo";
import PaymentMethodInfo from "./components/PaymentMethodInfo";

// 계약상태 - 계약체결 이전까지의 상태들
const contractState = ["CONTRACT_ACCEPT", "USE_APPROVAL", "USE_PROGRESS", "USE_COMPLETE", "TERMINATE_RECEIVED", "TERMINATE_COMPLETE"];

/**
 * 마이페이지 메인 > 신청/계약 내역 > 상세 화면
 */
const ContractDetail = () => {
  const { t } = useTranslation(["contract", "common"]);
  const { isMobile } = useMediaQueries();
  const location = useLocation();
  // const device = getUserDevice();

  const pathname = location.pathname;
  const pathnameArray = pathname.split("/");

  //배열로 만들고 배열안에 "partner"가 있는지 확인
  const partnerIndex = pathnameArray.findIndex((item) => item === "partner");
  //partner가 있으면 그 뒤에 있는 값을 가져오고 없으면 빈 문자열 반환
  const partnerCode = partnerIndex !== -1 ? pathnameArray[partnerIndex + 1] : "";

  const navigate = usePartnerNavigate();

  const { goBack } = useNavigateGoBack();

  const { postMessageGoBackNavigation, postWebViewPaymentMessage } = usePostMessage();

  // path variable 계약 id
  const { contractId } = useParams();
  // 로그인한 회원 정보
  const { member } = useMemberInfoState();

  // 신청/계약 목록 조회 api
  const { executeAsync: getContracts } = useApiOperation(getContractsAsync);
  // 지출 증빙 자료 저장
  const { executeAsync: saveExpenseProof } = useApiOperation(postExpenseProofAsync);
  // 지출 증빙 자료 조회
  const { executeAsync: getExpenseProof } = useApiOperation(getExpenseProofAsync);

  // 신청/계약 상세 조회 api
  const { executeAsync: getContract } = useApiOperation(getContractAsync);

  // 청구정보 결제현황 조회 api
  const { executeAsync: getBillingContractBills } = useApiOperation(getBillingContractBillsAsync);

  // 신청/계약 신용카드 등록
  const { executeAsync: postBillingAuthentication } = useApiOperation(postBillingAuthenticationAsync);
  // S2 페이레터 결제하기 API { billId, body : {pgcode: string }}
  const { executeAsync: postPayment } = useApiOperation(postPayletterPayment);

  // 상세화면 접근가능한지 여부
  const [isAllowedDetailPage, setIsAllowedDetailPage] = useState<boolean>(false);

  //결제 내역 및 취소네역
  const { executeAsync: fetchPaymentS2List } = useApiOperation(getBillsPaymentData);

  // 예약 정보 및 상태 확인
  const { executeAsync: getReservations } = useApiOperation(getReservationsAsync);

  // 계약 취소
  const { executeAsync: patchContractCancel } = useApiOperation(patchContractCancelAsync);

  // 신청/계약 상세
  const [contracts, setContracts] = useState<MemberContractModel[] | null>(null);
  const [contract, setContract] = useState<ContractModel | null>(null);
  const [isContractor, setIsContractor] = useState(false);
  // 청구정보 결제현황
  const [billingContractBill, setBillingContractBill] = useState<BillingContractBillModel>();
  // S2 청구정보 결제현황
  const [pList, setPList] = useState<any>([]);

  const [isClicked, setIsClicked] = useState(false);
  const [primaryQuotationName, setPrimaryQuotationName] = useState<any>({});
  const [alertModal, setAlertModal] = useState<Modal>({
    isOpen: false,
    message: "",
    subMessage: "",
  });
  const [confirmModal, setConfirmModal] = useState<VisitorModal>({
    isOpen: false,
  });

  const [documentProofRequest, setDocumentProofRequest] = useState<any>({
    identityNum: "",
    mediaList: [],
    taxInvoiceEmail: "",
    taxInvoicePhone: "",
    taxInvoiceDate: null,
  });
  const errorInit: any = useMemo(() => {
    return {
      error: {
        identityNum: false,
        identityType: false,
        mediaList: false,
        taxInvoiceEmail: false,
        taxInvoicePhone: false,
        taxInvoiceDate: false,
      },
      format: {
        identityNum: false,
        identityType: false,
        mediaList: false,
        taxInvoiceEmail: false,
        taxInvoicePhone: false,
        taxInvoiceDate: false,
      },
    };
  }, []);
  const [proofError, setProofError] = useState<any>(_.cloneDeep(errorInit));

  // 잔금이 0원인지 여부
  const isZeroBalanceAmount = useMemo(() => contract?.balanceAmount === 0, [contract]);

  // 스케줄 리스트에 rental item이 있는지 확인
  const rentalTypeSchedule = useMemo(() => {
    if (contract && contract.scheduleList.length > 0) {
      const rentalSchedule: any = contract?.scheduleList.find((item) => item.supplyType === "RENTAL");
      if (rentalSchedule) {
        return rentalSchedule;
      }
    }
  }, [contract]);

  // 무료 여부
  const isFree = useMemo(() => {
    return isFreeContract(contract);
  }, [contract]);
  // 신용캬드 등록 및 노출 여부
  const isPaymentCardExport = useMemo(() => {
    let _isPaymentCardExport = false;

    const allowedContractState = contractState.some((state) => state === contract?.contractStep);

    // billkey 여부 확인 - billKey 가 있으면 신용카드 이미 등록상태
    const isBillkey = rentalTypeSchedule?.isBillkey;

    //체크

    if (
      allowedContractState && //
      !isBillkey &&
      rentalTypeSchedule?.regularPgcode === "creditcard" &&
      isFree !== null &&
      !isFree
      // rentalTypeSchedule?.contractPayType === "PAY"&&
    ) {
      _isPaymentCardExport = true;
    }
    return _isPaymentCardExport;
  }, [contract, rentalTypeSchedule, isFree]);

  // 계좌 정보 노출여부 - 이용승인 / 이용중 / 해지접수 상태 시 노출 X
  const isAccountExport = useMemo(() => {
    let _isAccountExport = true;

    // 결제카드 등록 상태 boolean
    const isBillkey = rentalTypeSchedule?.isBillkey;

    if (isBillkey === true) {
      switch (contract?.contractStep) {
        case "USE_APPROVAL":
          _isAccountExport = false;
          break;
        case "USE_PROGRESS":
          _isAccountExport = false;
          break;
        case "TERMINATE_RECEIVED":
          _isAccountExport = false;
          break;
      }
    }
    return _isAccountExport;
  }, [contract?.contractStep, rentalTypeSchedule?.isBillkey]);

  // 결제 수단 변경 불가 여부 (해지접수 || 해지완료 상태일 때 비활성)
  const disabledPaymentChange = useMemo(() => {
    let disabled = false;

    if (contract?.contractStep === "USE_COMPLETE" || contract?.contractStep === "TERMINATE_COMPLETE") {
      disabled = true;
    }
    return disabled;
  }, [contract?.contractStep]);

  // 보증금 (1회차 원청구) 청구서
  const depositBill = useMemo(() => {
    if (!billingContractBill || (billingContractBill?.billList || []).length === 0) return null;
    return (billingContractBill?.billList || []).find((bill: BillModel) => bill.billOrder === 1 && !bill?.originBill.isOverdueBill);
  }, [billingContractBill]);

  const isZeroDepositBill = useMemo(() => {
    if (!depositBill) return false;
    return depositBill?.originBill.totalAmount === 0;
  }, [depositBill]);

  // 로그인한 사용자의 신청/계약 목록을 조회해서 상세화면 접근가능한 사용자 인지 여부 값 바인딩
  const fetchIsAllowedDetailPage = async (contractId: string, memberNo: string) => {
    const { data } = await getContracts({
      memberNo,
    });
    const contracts: Array<MemberContractModel> = data.data.content || [];
    setContracts(contracts);
    // 로그인한 사용자의 신청/계약 목록 중, 계약자인 것들 중, 해당 계약이 있는지 확인
    const findContract = contracts.find(
      (contract) => (contract.isContractor || contract.isAdmin) && String(contract.contractId) === String(contractId),
    );
    const _isAllowedDetailPage = !!findContract;
    if (_isAllowedDetailPage) {
      setIsContractor(findContract?.isContractor === true);
      setIsAllowedDetailPage(true);
    } else {
      setIsContractor(false);
      navigate("/court/error/page-not-allowed", { replace: true });
    }
  };

  // 신청 취소
  const contractCancel = async (contractId: string) => {
    if (!contractId) {
      throw Error("contract?.contractId is not found");
    }
    const { data } = await patchContractCancel({ contractId: Number(contractId) });
    if (data?.data?.contractId) {
      await refreshContract(String(contractId));
    }
  };

  // 계약 체결 상태라면 예약 상태 확인
  useEffect(() => {
    // 계약 체결 상태라면 예약 상태 확인
    if (isContractor && contract?.contractStep === "CONTRACT_ACCEPT") {
      checkReservationStatus(String(contract.contractId));
    }
  }, [contract?.contractId, contract?.contractStep, isContractor]);

  // 예약 상태 확인
  const checkReservationStatus = async (contractId: string) => {
    const { data, status } = await getReservations({ contractId, organizerOnly: true });

    if (status >= 200 && status <= 299 && data.data.content.length > 0) {
      const reservation = data.data.content[0];
      const reservationStatus = reservation?.status.code;
      if (reservationStatus === "ACCEPTED") {
        setConfirmModal({
          isOpen: true,
          title: t("detail.existingReservationTitle"),
          message: t("detail.existingReservationMessage"),
          type: "existing",
          onClose: () => {
            setConfirmModal({ isOpen: false });
            contractCancel(contractId);
          },
          onClick: () => {
            setConfirmModal({ isOpen: false });
            pollingReservationStatus();
          },
        });
      }
    }
  };

  // 예약 상태가 접수 상태일 때 점유를 위한 polling
  const pollingReservationStatus = async () => {
    // 타이머 ID를 저장할 변수
    let timeoutId: NodeJS.Timeout;
    let isPollingCancelled = false;

    // 폴링 중단 함수
    const stopPolling = () => {
      clearTimeout(timeoutId);
      isPollingCancelled = true;
    };

    // 폴링을 수행하는 함수
    const pollStatus = async () => {
      // 폴링 중단 플래그 확인
      if (isPollingCancelled) {
        return;
      }

      try {
        const { data, status } = await getReservations({ contractId, organizerOnly: true });

        if (status >= 200 && status <= 299) {
          const reservation = data.data.content[0];
          const reservationStatus = reservation.status.code;

          switch (reservationStatus) {
            case "ACKNOWLEDGED": {
              // 성공 시 타이머 해제 및 폴링 중단
              clearTimeout(timeoutId);
              isPollingCancelled = true;

              // 성공 페이지로 이동
              navigate(`/court/mypage/contracts/${contractId}`, {
                state: "quotation",
              });
              return;
            }
            case "INCOMPLETE": {
              const incompleteReasons = reservation.incompleteReasons || [];
              setConfirmModal({
                isOpen: true,
                title: t("detail.reservationFailTitle"),
                message: t("detail.reservationFailMessage"),
                type: "fail",
                payload: incompleteReasons,
              });
              return;
            }
          }

          // ACKNOWLEDGED가 아니고 폴링이 중단되지 않은 경우에만 계속
          if (!isPollingCancelled) {
            setTimeout(pollStatus, 500);
          }
        }
      } catch (error) {
        console.error("예약 상태 확인 중 오류 발생:", error);
        stopPolling();
      }
    };

    // 타임아웃 설정 (15초)
    timeoutId = setTimeout(() => {
      // 폴링 중단 설정
      isPollingCancelled = true;

      setConfirmModal({
        isOpen: true,
        title: t("detail.processingDelayTitle"),
        message: t("detail.processingDelayMessage"),
        type: "delay",
        onClose: () => {
          setConfirmModal({ isOpen: false });
          contractCancel(String(contractId));
        },
        onClick: () => {
          setConfirmModal({ isOpen: false });
          // 재시도 시 처음부터 다시 시작
          pollingReservationStatus();
        },
      });
    }, 15000);

    // 폴링 시작
    pollStatus();
  };

  // 신청/계약 상세 조회
  const fetchContract = async (contractId: string) => {
    const { data } = await getContract({ contractId });
    const contract = data?.data || null;
    setContract(contract);
  };

  const fetchExpenseProof = async (contractId: string) => {
    const { data: data2 } = await getExpenseProof({ contractId });
    const expenseProofRequest = data2?.data?.expenseProofRequest;

    if (expenseProofRequest && expenseProofRequest.mediaList !== null) {
      expenseProofRequest.mediaList?.forEach((dt: any) => {
        dt["cmdType"] = "U";
      });
      setDocumentProofRequest(expenseProofRequest);
    }
  };

  const refreshContract = async (contractId: string) => {
    await fetchContract(contractId);
    if (isContractor) {
      await fetchExpenseProof(contractId);
    }
  };

  // 청구정보 결제현황 조회
  const fetchBillingContractBills = async (contractId: number) => {
    const { data } = await getBillingContractBills({ contractId });

    setBillingContractBill(data?.data);
  };

  // 신용카드 결제등록 url 조회
  const fetchPaymentUrl = async () => {
    const rentalType = contract?.scheduleList.find((item) => item.supplyType === "RENTAL");
    if (contract && rentalType) {
      let envType = "web";
      if (window.ReactNativeWebView) {
        envType = "taap";
      }

      let params: any = { scheduleId: String(rentalType.scheduleId), envType };
      if (partnerCode !== "") {
        params = { scheduleId: String(rentalType.scheduleId), envType, partnerCode };
      }

      const { data } = await postBillingAuthentication(params);

      if (data.data) {
        if (window.ReactNativeWebView) {
          postWebViewPaymentMessage(data.data.mobileUrl);
        } else {
          if (isMobile) {
            // 새창 없이 그대로 페이지 이동
            window.location.href = data.data.mobileUrl;
          } else {
            // 새창 없이 그대로 페이지 이동
            window.location.href = data.data.onlineUrl;
          }
        }
      }
    }
  };

  //세금계싼서 벨리데이션 체크
  const validationChecker = (documentProofRequest: any) => {
    let rtnFlag = true;
    const check = _.cloneDeep(errorInit);
    if (documentProofRequest.expenseProofType === "TAX_INVOICE") {
      if (documentProofRequest.identityNum === null || documentProofRequest.identityNum === "") {
        rtnFlag = false;
        check.error.identityNum = true;
        check.format.identityNum = false;
      } else {
        check.error.identityNum = false;
        if (documentProofRequest.identityNum.length === 10) {
          console.log("정상");
          check.format.identityNum = false;
          check.error.identityNum = false;
        } else {
          console.log("비정상");
          check.format.identityNum = true;
          check.error.identityNum = false;
          rtnFlag = false;
        }
      }
      if (documentProofRequest.mediaList.length === 0) {
        rtnFlag = false;
        check.error.mediaList = true;
      } else {
        check.error.mediaList = false;
      }
      if (documentProofRequest.taxInvoiceEmail === null || documentProofRequest.taxInvoiceEmail === "") {
        rtnFlag = false;
        check.error.taxInvoiceEmail = true;
      } else {
        check.error.taxInvoiceEmail = false;
        if (!emailFormat(documentProofRequest.taxInvoiceEmail)) {
          rtnFlag = false;
          check.format.identityNum = true;
        } else {
          check.format.identityNum = false;
        }
      }

      if (documentProofRequest.taxInvoicePhone === null || documentProofRequest.taxInvoicePhone === "") {
        rtnFlag = false;
        check.error.taxInvoicePhone = true;
      } else {
        check.error.taxInvoicePhone = false;
        if (!linePhoneFormat(documentProofRequest.taxInvoicePhone)) {
          rtnFlag = false;
          check.format.identityNum = true;
        } else {
          check.format.identityNum = false;
        }
      }

      if (documentProofRequest.taxInvoiceDate === null || documentProofRequest.taxInvoiceDate === "") {
        rtnFlag = false;
        check.error.taxInvoiceDate = true;
      } else {
        check.error.taxInvoiceDate = false;
      }
    }

    setProofError({ ...check });
    return rtnFlag;
  };

  const sendPgCode = async (pgcode: string) => {
    const rentalType = contract?.scheduleList.find((item) => item.supplyType === "RENTAL");
    if (contract && rentalType) {
      console.log("여기냐3");
      let billId = rentalType.recentBill?.billId!;
      let envType = "web";
      if (window.ReactNativeWebView) {
        envType = "taap";
      }
      let params: any = { billId, pgcode, envType };
      if (partnerCode !== "") {
        params = { billId, pgcode, envType, partnerCode };
      }

      const { data } = await postPayment(params);

      if (data.data) {
        if (data.data.isPaymentRequired === false) {
          window.location.reload();
        } else {
          if (window.ReactNativeWebView) {
            postWebViewPaymentMessage(data.data.mobileUrl);
          } else {
            if (isMobile) {
              // 새창 없이 그대로 페이지 이동
              window.location.href = data.data.mobileUrl;
            } else {
              // 새창 없이 그대로 페이지 이동
              window.location.href = data.data.onlineUrl;
            }
          }
        }
      }
    }
  };

  // 신용카드 결제 or 가상계좌 url 조회
  const fetchPaymentUrlS2 = async (pgcode: string, documentProofRequest: any) => {
    const checkData = _.cloneDeep(documentProofRequest);
    console.log("checkData", checkData);
    let result = true;
    if (pgcode !== "creditcard") {
      result = validationChecker(checkData);
      if (result === true) {
        if (checkData.taxInvoiceDate !== null && checkData.taxInvoiceDate !== "") {
          checkData.taxInvoiceDate = moment(checkData.taxInvoiceDate).format(YmdFormat.YYYY_MM_DD) + "T00:00:00.000+09:00";
        }

        if (checkData.contractId !== null && Number(checkData.contractId) > 0) {
          checkData.cmdType = "U";
        } else {
          checkData.cmdType = "C";
        }
        if (pgcode === "creditcard") {
          checkData.isDeleted = true;
        }
        if (checkData.mediaList.length > 0) {
          checkData.mediaList.forEach((media: any) => {
            delete media.blobUrl;
          });
        }
      }
    }
    //지출 증비 관련 저장

    if (result === true) {
      setProofError({ ...errorInit });
      if (pgcode !== "creditcard") {
        const saveExpense = await saveExpenseProof({ contractId: String(contract!.contractId), expenseProofRequest: checkData });
        if (saveExpense.status > 199 && saveExpense.status < 400) {
          await sendPgCode(pgcode);
        }
      } else {
        console.log("여기냐");
        await sendPgCode(pgcode);
      }
    }
  };

  useEffect(() => {
    window.court.webViewFunction.onPaymentCompleted = (queryParam?: string) => {
      const param: { [prop: string]: string } = JSON.parse(queryParam || "{}");

      const searchParams = new URLSearchParams(param).toString();
      let path = "/court/payment/result";

      if (searchParams) {
        path += `?${searchParams}`;
      }

      navigate(path, {
        replace: true,
      });
    };
  }, []);

  useEffect(() => {
    if (contractId && member && member.memberNo) {
      // 로그인한 사용자의 신청/계약 목록을 조회해서 상세화면 접근가능한 사용자 인지 여부 값 바인딩
      fetchIsAllowedDetailPage(contractId, member.memberNo);
    }
  }, [contractId, member]);

  useEffect(() => {
    // 상세화면 접근가능할 경우 조회
    if (isAllowedDetailPage && contractId) {
      // 신청/계약 상세 조회
      refreshContract(contractId);
    }
  }, [isAllowedDetailPage, contractId, isContractor]);

  useEffect(() => {
    // 유료일 경우에만 조회
    if (isContractor && contract && contract?.contractId && !isFree) {
      // 청구정보 결제현황 조회
      fetchBillingContractBills(contract.contractId);
    }
  }, [contract, isFree, isContractor]);

  // 버튼 클릭시 더블클릭 방지
  const handleClickPayment = () => {
    // 이미 클릭되었다면 더블 클릭을 방지
    if (isClicked) {
      return;
    }

    // 버튼 클릭 처리
    setIsClicked(true);

    // 클릭 이후 1.5초 동안 다시 클릭 불가능하도록 설정
    setTimeout(() => {
      setIsClicked(false);
    }, 1500);

    fetchPaymentUrl();
  };
  // 버튼 클릭시 더블클릭 방지
  const handleClickPaymentS2 = (pgcode: string, documentProofRequest: any) => {
    // 이미 클릭되었다면 더블 클릭을 방지
    if (isClicked) {
      return;
    }

    // 버튼 클릭 처리
    setIsClicked(true);

    // 클릭 이후 1.5초 동안 다시 클릭 불가능하도록 설정
    setTimeout(() => {
      setIsClicked(false);
    }, 1500);

    fetchPaymentUrlS2(pgcode, documentProofRequest);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentStatus, setPaymentStatus] = useState<any>([]);
  const { executeAsync: fetchPaymentData } = useApiOperation(getPaymentData);

  const paymentList = async (contractId: string) => {
    const response = await fetchPaymentData({ contractId });
    if (response.data.data.content && response.data.data.content.length > 0) {
      setPaymentStatus(response.data.data.content);
    }
  };

  const handleClickExpenseProofCashBill = async () => {
    if (documentProofRequest.taxInvoiceDate !== null && documentProofRequest.taxInvoiceDate !== "") {
      documentProofRequest.taxInvoiceDate = moment(documentProofRequest.taxInvoiceDate).format(YmdFormat.YYYY_MM_DD) + "T00:00:00.000+09:00";
    }

    if (documentProofRequest.contractId !== null && Number(documentProofRequest.contractId) > 0) {
      documentProofRequest.cmdType = "U";
    } else {
      documentProofRequest.cmdType = "C";
    }

    const saveExpense = await saveExpenseProof({ contractId: String(contract!.contractId), expenseProofRequest: documentProofRequest });
    if (saveExpense.status > 199 && saveExpense.status < 400) {
      setAlertModal({
        ...alertModal,
        isOpen: true,
        message: t("detail.cashReceiptRequestCompleteTitle"),
        subMessage: t("detail.cashReceiptRequestCompleteMessage"),
      });
    }
  };

  const findS2Payments = async (billId: string, pgcode: string) => {
    let isSuccessOnly = true;
    if (pgcode === "virtualaccount") {
      isSuccessOnly = false;
    }
    const response = await fetchPaymentS2List({ billId, pgcode, isSuccessOnly });
    if (response.data.data.content) {
      setPList(response.data.data.content);
    }
  };

  useEffect(() => {
    if (isContractor && contract) {
      paymentList(String(contract?.contractId));
      if (rentalTypeSchedule?.recentBill) {
        findS2Payments(rentalTypeSchedule?.recentBill?.billId!, rentalTypeSchedule?.recentBill?.pgcode!);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  const showPaymentRegistration = isContractor && contract?.spaceProductType !== "TIME_COURT" && isPaymentCardExport;
  const showRentalPaymentContent = isContractor && contract?.spaceProductType !== "TIME_COURT" && !isZeroDepositBill;
  const showPaymentMethodInfo = isContractor && contract?.spaceProductType !== "TIME_COURT" && Boolean(rentalTypeSchedule?.isBillkey);
  const showServiceTypePaymentInfo = isContractor && contract?.spaceProductType === "TIME_COURT" && Boolean(rentalTypeSchedule?.recentBill);

  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header
        headerType="CLOSE"
        // title="상세 내역"
        title={t("list.applicationContract")}
        onClickCloseButton={() => {
          //리액트 네이티브 웹뷰가 아니면 state 체크해서 -1 처리 or 신청/계약 목록으로 이동 처리
          if (!window.ReactNativeWebView) {
            if (location.state && location.state === "mypage") {
              navigate("/court/mypage/contracts");
            } else if (location.state && location.state === "quotation") {
              navigate(`/court/mypage/main`);
            } else {
              goBack();
            }
          } else {
            postMessageGoBackNavigation();
          }
        }}
      />
      {/* <BaseButton title="이용신청" onClick={() => {navigate}} /> */}
      <div className="contract-page">
        <h1 className="base-title">{t("list.applicationContract")}</h1>
        <article className="with-backward-floating">
          {/* 결제수단 */}
          {showPaymentRegistration && <PaymentInfo handleClickPayment={handleClickPayment} />}
          {/* 보 */}
          <div className={showPaymentRegistration ? "top-divider" : ""}>
            <BasicInfo
              contract={contract}
              fetchContract={refreshContract}
              isContractor={isContractor}
              setPrimaryQuotationName={setPrimaryQuotationName}
            />
          </div>

          {/* 계약 정보 */}
          {isContractor && <ContractInfo contract={contract} />}

          {/* 공간임대 일때 */}
          {showRentalPaymentContent &&
            (isZeroBalanceAmount ? (
              <MyPaymentStatus contract={contract} billingContractBill={billingContractBill} />
            ) : (
              <>
                {/* 이용승인 상태 이후 && 신용카드 등록되어있을 시 계좌정보 노출 X */}
                {isAccountExport && <AccountInfo contract={contract} billingContractBill={billingContractBill} />}
                <MyPaymentStatus contract={contract} billingContractBill={billingContractBill} />
              </>
            ))}

          {/* 결제수단 */}
          {showPaymentMethodInfo && (
            <PaymentMethodInfo
              scheduleInfo={rentalTypeSchedule}
              handleClickPayment={handleClickPayment}
              disabledPaymentChange={disabledPaymentChange}
            />
          )}

          {showServiceTypePaymentInfo && (
            <ServiceTypePaymentInfo
              contract={contract}
              contracts={contracts}
              paymentStatus={pList}
              primaryQuotationName={primaryQuotationName}
              recentBill={rentalTypeSchedule?.recentBill}
              handleClickPaymentS2={handleClickPaymentS2}
              documentProofRequest={documentProofRequest}
              setDocumentProofRequest={setDocumentProofRequest}
              billingContractBill={billingContractBill}
              handleClickExpenseProofCashBill={handleClickExpenseProofCashBill}
              proofError={proofError}
              setProofError={setProofError}
            />
          )}
          {/* 한컴타운 코워킹 연동 (모바일 기기에만 출력) */}
          {/* {device !== "other" && <HancomSpace contract={contract} />} */}
        </article>
        {alertModal.isOpen && (
          <ConfirmModal
            isOpen={alertModal.isOpen}
            btnRightTitle={t("common:confirm")}
            onClick={() => {
              setAlertModal({ ...alertModal, isOpen: false });

              window.location.reload();
            }}
          >
            <div>
              <p className="font18 font-weight-600">{alertModal?.message}</p>
              <p className="font18">{alertModal?.subMessage}</p>
            </div>
          </ConfirmModal>
        )}

        {confirmModal.isOpen && (
          <IssueModalRenderer
            isOpen={true}
            title={confirmModal.title}
            message={confirmModal.message}
            type={confirmModal.type as "delay" | "existing" | "fail"}
            onClick={() => {
              confirmModal.onClick && confirmModal.onClick();
            }}
            // onClose={() => setConfirmModal({ isOpen: false })}
            onClose={() => (confirmModal.onClose ? confirmModal.onClose() : setConfirmModal({ isOpen: false }))}
            payload={confirmModal.payload}
          />
        )}
      </div>
    </>
  );
};

export default ContractDetail;
